import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Plane, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { SectionHeading } from "@/components/PageShell";
import { PARTNERS } from "@/constants";

const fmt = (iso) => new Date(iso).toLocaleString("en-GB", { weekday: "short", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });

export const EmptyLegCard = ({ leg, index = 0 }) => (
  <motion.article
    initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-40px" }} transition={{ duration: 0.6, delay: (index % 3) * 0.08 }}
    data-testid={`empty-leg-card-${leg.id}`}
    className="relative bg-surface border border-white/10 rounded-sm overflow-hidden flex flex-col sm:flex-row transition-colors duration-500 hover:border-gold/50"
  >
    <div className="flex-1 min-w-0 p-6 lg:p-7">
      <div className="flex items-center justify-between text-[10px] uppercase tracking-[0.25em] text-gold">
        <span>{leg.kind === "offer" ? "Charter offer" : "Empty leg"}</span>
        <span className="text-white/40">{leg.aircraft_class}</span>
      </div>
      <div className="mt-5 flex items-center gap-4">
        <div className="min-w-0">
          <p className="font-serif text-xl lg:text-2xl text-white leading-tight">{leg.from_location}</p>
        </div>
        <div className="flex-1 min-w-[48px] flex items-center gap-2 text-white/30">
          <span className="flex-1 h-px bg-white/15" /><Plane size={14} className="text-gold rotate-45" /><span className="flex-1 h-px bg-white/15" />
        </div>
        <div className="min-w-0 text-right">
          <p className="font-serif text-xl lg:text-2xl text-white leading-tight">{leg.to_location}</p>
        </div>
      </div>
      <dl className="mt-6 grid grid-cols-3 gap-4 text-xs">
        <div><dt className="text-white/40 uppercase tracking-wider text-[9px]">Departure</dt><dd className="text-white/85 mt-1">{fmt(leg.departure_at)}</dd></div>
        <div><dt className="text-white/40 uppercase tracking-wider text-[9px]">Aircraft</dt><dd className="text-white/85 mt-1">{leg.aircraft}</dd></div>
        <div><dt className="text-white/40 uppercase tracking-wider text-[9px]">Seats</dt><dd className="text-white/85 mt-1">Up to {leg.seats}</dd></div>
      </dl>
    </div>
    <div className="sm:w-52 shrink-0 border-t sm:border-t-0 sm:border-l border-dashed border-white/15 p-6 flex sm:flex-col items-center sm:items-start justify-between gap-4 bg-black/30">
      <div>
        <p className="text-[9px] uppercase tracking-[0.25em] text-white/40">Whole aircraft</p>
        <p className="font-serif text-2xl text-gold mt-1" data-testid={`empty-leg-price-${leg.id}`}>Enquire for price</p>
        <p className="text-[10px] text-white/30 mt-1">availability confirmed by the team</p>
      </div>
      <Link to={`/enquire/charter?trip_type=One-way&departure=${encodeURIComponent(leg.from_location)}&arrival=${encodeURIComponent(leg.to_location)}&date=${encodeURIComponent(fmt(leg.departure_at))}&passengers=${leg.seats}&aircraft_class=${encodeURIComponent(leg.aircraft_class || "No preference")}&budget=${encodeURIComponent(`Empty leg enquiry: ${leg.aircraft}`)}`}
            data-testid={`empty-leg-request-${leg.id}`}
            className="inline-flex items-center gap-2 bg-gold text-black text-[11px] uppercase tracking-[0.2em] px-4 py-2.5 rounded-sm hover:bg-gold-light transition-colors whitespace-nowrap">
        Request <ArrowRight size={12} />
      </Link>
    </div>
  </motion.article>
);

export const PartnersStrip = () => (
  <div className="mt-16 pt-8 border-t border-white/5 flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-10" data-testid="partners-strip">
    <p className="text-[10px] uppercase tracking-[0.25em] text-white/40">Our sourcing partners</p>
    <div className="flex gap-8">
      {PARTNERS.map((p) => (
        <a key={p.name} href={p.url} target="_blank" rel="noopener noreferrer" data-testid={`partner-link-${p.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
           className="font-serif text-lg text-white/70 hover:text-gold transition-colors">{p.name}</a>
      ))}
    </div>
  </div>
);

export const EmptyLegs = ({ limit = 3, showAllLink = true }) => {
  const [legs, setLegs] = useState([]);
  useEffect(() => { api("/empty-legs").then(setLegs).catch(() => {}); }, []);
  return (
    <section id="empty-legs" data-testid="empty-legs-section" className="py-24 lg:py-32 bg-surface border-t border-white/5">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
          <SectionHeading overline="Empty legs & offers" title="Positioning flights at a fraction of the charter price">
            Our operator partners' repositioning flights, published as they become available. Enquire for pricing and the team will confirm availability.
          </SectionHeading>
          {showAllLink && <Link to="/empty-legs" className="mb-14 text-[11px] uppercase tracking-[0.2em] text-white/60 hover:text-gold transition-colors" data-testid="empty-legs-all-link">All empty legs</Link>}
        </div>
        {legs.length === 0 ? (
          <p className="text-white/50 text-sm" data-testid="empty-legs-empty">No empty legs published right now. Request a flight and we will source options for you.</p>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            {legs.slice(0, limit || legs.length).map((l, i) => <EmptyLegCard key={l.id} leg={l} index={i} />)}
          </div>
        )}
        <PartnersStrip />
      </div>
    </section>
  );
};
